import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, invalidateData, useMe, useMeta } from '../../api.ts';
import { bandFromFrequency } from '../../../shared/format.ts';
import { qsoInputSchema, type QsoFormValues } from '../../../shared/schemas.ts';
import { CountrySelect, ErrorBox, Field, Help, PageHeader, Spinner, useDocumentTitle, useToast } from '../../components/ui.tsx';

function nowUtc() {
  const d = new Date().toISOString();
  return { date: d.slice(0, 10), time: d.slice(11, 16) };
}

export function ImportSinglePage() {
  useDocumentTitle('Lisää QSO');
  const { data: me } = useMe();
  const meta = useMeta();
  const toast = useToast();
  const { register, handleSubmit, formState, control, reset, setValue, getValues } = useForm<
    QsoFormValues,
    unknown,
    z.output<typeof qsoInputSchema>
  >({
    resolver: zodResolver(qsoInputSchema),
    defaultValues: { callSign: '', ...nowUtc(), band: '', mode: '', countryId: '', frequency: '' },
  });
  const e = formState.errors;
  const save = useMutation({
    mutationFn: (d: z.output<typeof qsoInputSchema>) =>
      api.post<{ saved: number }>(`/users/${encodeURIComponent(me!.id)}/qsos`, { rows: [d] }),
    onSuccess: (r, d) => {
      if (!r.saved) return toast.error(new Error('QSO:ta ei voitu tallentaa'));
      toast.ok(`${d.callSign} tallennettu`);
      invalidateData();
      // seuraavaa yhteyttä varten pidetään band, moodi ja päivä
      reset({ ...getValues(), callSign: '', countryId: '', frequency: '', time: nowUtc().time });
    },
    onError: toast.error,
  });

  return (
    <div className="page page-narrow">
      <PageHeader title="Lisää QSO" sub="Ajat UTC-ajassa." actions={<Help name="YksittainenQSO" />} />
      <form className="card form" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
        <div className="form-grid">
          <Field label="Kutsu" error={e.callSign} htmlFor="callSign">
            <input id="callSign" className="input mono" autoCapitalize="characters" autoFocus {...register('callSign')} />
          </Field>
          <Field label="Päivä" error={e.date} htmlFor="date">
            <input id="date" type="date" className="input" {...register('date')} />
          </Field>
          <Field label="Aika (UTC)" error={e.time} htmlFor="time">
            <input id="time" type="time" className="input" {...register('time')} />
          </Field>
          <Field label="Taajuus (MHz)" error={e.frequency} hint="Valinnainen – täyttää bandin" htmlFor="frequency">
            <input
              id="frequency"
              className="input mono"
              inputMode="decimal"
              {...register('frequency', {
                onBlur: (ev) => {
                  const band = bandFromFrequency(Number(String(ev.target.value).replace(',', '.')));
                  if (band && !getValues('band')) setValue('band', band, { shouldValidate: true });
                },
              })}
            />
          </Field>
          <Field label="Band" error={e.band} htmlFor="band">
            <select id="band" {...register('band')} aria-invalid={!!e.band}>
              <option value="">Valitse…</option>
              {meta.data?.bands.map((b) => (
                <option key={b.band} value={b.band}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Moodi" error={e.mode} htmlFor="mode">
            <select id="mode" {...register('mode')} aria-invalid={!!e.mode}>
              <option value="">Valitse…</option>
              {meta.data?.modes.map((m) => (
                <option key={m.mode} value={m.mode}>
                  {m.parentMode ? `${m.mode} (${m.parentMode})` : m.mode}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Maa" error={e.countryId} htmlFor="countryId">
          <Controller
            control={control}
            name="countryId"
            render={({ field }) => (
              <CountrySelect
                id="countryId"
                value={field.value ? Number(field.value) : null}
                invalid={!!e.countryId}
                onChange={(v) => field.onChange(v ?? '')}
              />
            )}
          />
        </Field>
        <ErrorBox error={save.error} />
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : 'Tallenna QSO'}
          </button>
        </div>
      </form>
    </div>
  );
}
